// InspectionGallery.jsx (Código ajustado para o formato do JSON)

import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { TbLoader } from "react-icons/tb";
import PublicLayout from "../../Layouts/PublicLayout";
import GroupedContainer from "../../Components/GroupedContainer";
import InfoSection from "../../Components/InfoSection";

export default function InspectionGallery() {
  const { loadItemId, inspectionType } = useParams();

  const [photos, setPhotos] = useState([]);
  const [vehicleInfo, setVehicleInfo] = useState(null);
  const [orderId, setOrderId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    const fetchInspectionData = async () => {
      try {
        // O endpoint parece ser /api/loaditem/public/{id}, vou mantê-lo
        const response = await axios.get(
          `https://new.cajuu.app/api/loaditem/public/${loadItemId}`
        );
        
        // ✨ MUDANÇA AQUI: Ajustando a forma como lemos os dados da API
        // O objeto com os dados do veículo está em response.data.data
        const vehicleData = response.data.data;
        
        // O order_id está aninhado dentro de load_transport
        const orderIdFromApi = vehicleData.load_transport?.order_id;

        // As fotos estão diretamente em vehicleData.inspection_photos
        const photosToFilter = vehicleData.inspection_photos || [];
        const filteredPhotos = photosToFilter.filter(
          (photo) => photo.inspection_type === inspectionType
        );

        if (filteredPhotos.length === 0) {
          setError(`No ${inspectionType} photos were found for this vehicle.`);
        }

        // ✨ MUDANÇA AQUI: Setando os estados com os dados nos locais corretos
        setPhotos(filteredPhotos);
        setVehicleInfo(vehicleData); // O objeto vehicleData contém make, model, year, etc.
        setOrderId(orderIdFromApi);

      } catch (err) {
        console.error("Failed to fetch inspection data:", err);
        setError("Vehicle data not found or an error occurred.");
      } finally {
        setLoading(false);
      }
    };

    if (loadItemId) {
      fetchInspectionData();
    } else {
      setError("Vehicle ID is missing from the URL.");
      setLoading(false);
    }
  }, [loadItemId, inspectionType]);

  const getImageUrl = (path) => {
    if (!path) return "/img/cajuu_logo.png";
    return `https://new.cajuu.app/storage/${path}`;
  };

  const nextImage = () => {
    if (photos.length === 0) return;
    setSelectedIndex((prev) => (prev + 1) % photos.length);
  };

  const previousImage = () => {
    if (photos.length === 0) return;
    setSelectedIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="flex justify-center items-center h-screen">
          <TbLoader className="animate-spin text-4xl" />
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        <GroupedContainer>
          <InfoSection title={`INSPECTION PHOTOS - ${inspectionType?.toUpperCase()}`} />
        </GroupedContainer>

        <div className="mt-4 sm:mt-6 lg:mt-8">
          <div className="bg-[#1a1a1a] p-4 sm:p-6 lg:p-8 rounded-lg">
            <div className="max-w-6xl mx-auto">
              {error ? (
                <div className="text-center text-yellow-400">{error}</div>
              ) : (
                <>
                  <div className="bg-[#2a2a2a] rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
                      <span className="text-base sm:text-lg whitespace-nowrap">
                        Order ID: #{orderId}
                      </span>
                      {vehicleInfo && (
                        <div className="flex items-center gap-3">
                          <img
                            src={getImageUrl(vehicleInfo.profile_picture_path) || "/img/cajuu_logo.png"}
                            width={50}
                            height={50}
                            alt="Vehicle profile"
                            className="mr-3 rounded object-cover flex-shrink-0"
                          />
                          <div className="text-xs sm:text-sm">
                            <div>{vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}</div>
                            <div className="text-gray-400">VIN: {vehicleInfo.vin || 'N/A'}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="relative aspect-[16/9] mb-4 sm:mb-6 bg-[#2a2a2a] rounded-lg flex items-center justify-center">
                    {photos.length > 0 ? (
                      <img src={getImageUrl(photos[selectedIndex].path)} alt={`Inspection photo ${selectedIndex + 1}`} className="w-full h-full object-contain rounded-lg"/>
                    ) : (
                      <p className="text-gray-400">No images to display.</p>
                    )}
                  </div>
                  
                  {photos.length > 1 && (
                    <div className="relative">
                      <button onClick={previousImage} className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-black/50 p-1 sm:p-2 rounded-full hover:bg-black/70 transition-colors" aria-label="Previous image"><ChevronLeft className="h-4 w-4 sm:h-6 sm:w-6" /></button>
                      <div className="relative z-10 flex gap-2 overflow-x-auto px-8 sm:px-10 lg:px-12 py-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">
                        {photos.map((photo, index) => (
                          <button key={photo.id} onClick={() => setSelectedIndex(index)} className={`flex-shrink-0 relative rounded-md overflow-hidden ${selectedIndex === index ? "ring-2 ring-blue-500" : "ring-1 ring-gray-600/50"}`}>
                            <img src={getImageUrl(photo.path)} alt={`Thumbnail ${index + 1}`} className="w-16 h-16 sm:w-20 sm:h-20 object-cover"/>
                          </button>
                        ))}
                      </div>
                      <button onClick={nextImage} className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-black/50 p-1 sm:p-2 rounded-full hover:bg-black/70 transition-colors" aria-label="Next image"><ChevronRight className="h-4 w-4 sm:h-6 sm:w-6" /></button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}